import { nanoid } from "nanoid";
import type { PaymentKitEnvironment } from "../types";

declare global {
  interface Window {
    __paymentKitRequestId__?: string;
  }
}

/**
 * Maps PaymentKit environment to request ID env slug.
 * This must match the backend's env slug format.
 */
const ENV_TO_SLUG: Record<PaymentKitEnvironment, string> = {
  local: "dev",
  tunnel: "dev",
  sandbox: "prod",
  staging: "stg",
  production: "prod",
};

/**
 * Generates a checkout request ID for correlating all API calls within a checkout session.
 *
 * Format: pk_{env}_{nanoid(12)}
 * - pk = PaymentKit.js (client-generated)
 * - env = dev, stg, or prod
 * - nanoid(12) = 12 character random string
 *
 * Example: pk_stg_a1b2c3d4e5f6
 */
export function generateCheckoutRequestId(environment: PaymentKitEnvironment): string {
  const envSlug = ENV_TO_SLUG[environment] ?? "dev";
  const randomPart = nanoid(12);
  return `pk_${envSlug}_${randomPart}`;
}

/**
 * Gets or creates a checkout request ID, storing it in window for idempotency.
 *
 * This ensures the same request ID is used across:
 * - Multiple PaymentKit instantiations on the same page
 * - All analytics events and API calls within a checkout session
 *
 * The ID is generated once on first call and reused thereafter.
 */
export function getOrCreateCheckoutRequestId(environment: PaymentKitEnvironment): string {
  if (typeof window !== "undefined" && window.__paymentKitRequestId__) {
    return window.__paymentKitRequestId__;
  }

  const requestId = generateCheckoutRequestId(environment);

  if (typeof window !== "undefined") {
    window.__paymentKitRequestId__ = requestId;
  }

  return requestId;
}

/**
 * Creates RequestOptions with the x-request-id header set.
 */
export function withRequestId(checkoutRequestId: string) {
  return {
    headers: {
      "x-request-id": checkoutRequestId,
    },
  };
}
